/**
 * useActivityTracking
 * ===================
 * Tracks manager activity and sends events to /api/v1/sales/activity/.
 *
 *   start()                      — begin tracking on this page
 *   stop()                       — stop (unmount)
 *   writingStart(source)         — called when user starts typing in a form
 *   writingEnd(source)           — called when user stops typing (debounced 3s)
 *   heartbeat()                  — called every 30s (keeps presence fresh)
 *
 * All errors are swallowed with console.warn — tracking must never break
 * the UX. Integrates with useNotify only for the "you look idle" banner.
 */
import { ref, onBeforeUnmount } from 'vue'
import apiClient from '@/api/client'
import { salesApi } from '@/modules/sales/api/salesApi'
import { isTechPreview } from '@/preview/techPreview'

const DEFAULT_HEARTBEAT_MS = 30_000
const DEFAULT_WRITING_DEBOUNCE_MS = 3_000
const IDLE_END_MIN_INTERVAL_MS = 60_000  // don't spam idle_end

// Module-level cache of the configuration (loaded once per tab).
// Settings live in system_settings and are editable via /sales/settings/.
let configPromise = null
const runtimeConfig = {
  heartbeat: DEFAULT_HEARTBEAT_MS,
  writingDebounce: DEFAULT_WRITING_DEBOUNCE_MS,
}
function _loadConfigOnce() {
  if (configPromise) return configPromise
  configPromise = salesApi.getSettings()
    .then(({ data }) => {
      for (const s of (data?.settings || [])) {
        if (s.key === 'sales.heartbeat_interval_ms' && s.value > 0) runtimeConfig.heartbeat = s.value
        if (s.key === 'sales.writing_debounce_ms' && s.value > 0) runtimeConfig.writingDebounce = s.value
      }
    })
    .catch(() => {})
  return configPromise
}

export function useActivityTracking() {
  const writingState = ref({})
  const debounceTimers = {}
  let heartbeatTimer = null
  let started = false

  function _safe(fn) {
    try { return fn() } catch (e) { console.warn('[sales/activity]', e) }
  }

  let lastIdleEndAt = 0
  function maybeIdleEnd() {
    const now = Date.now()
    if (now - lastIdleEndAt < IDLE_END_MIN_INTERVAL_MS) return
    lastIdleEndAt = now
    _safe(() => apiClient.post('/sales/activity/ping/', { event_type: 'idle_end' }))
  }

  function writingStart(source = 'unknown') {
    // Any typing also means the user is NOT idle anymore — close any open idle.
    maybeIdleEnd()
    if (writingState.value[source]) {
      clearTimeout(debounceTimers[source])
      debounceTimers[source] = setTimeout(() => writingEnd(source), runtimeConfig.writingDebounce)
      return
    }
    writingState.value[source] = true
    _safe(() => salesApi.writing(true, source))
    debounceTimers[source] = setTimeout(() => writingEnd(source), runtimeConfig.writingDebounce)
  }

  function writingEnd(source = 'unknown') {
    if (!writingState.value[source]) return
    writingState.value[source] = false
    clearTimeout(debounceTimers[source])
    _safe(() => salesApi.writing(false, source))
  }

  function heartbeat() {
    _safe(() => salesApi.ping({ event_type: 'page_heartbeat' }))
  }

  function start() {
    if (started) return
    started = true
    _loadConfigOnce().finally(() => {
      heartbeat()
      if (heartbeatTimer) clearInterval(heartbeatTimer)
      heartbeatTimer = setInterval(heartbeat, runtimeConfig.heartbeat)
    })
  }

  function stop() {
    started = false
    if (heartbeatTimer) clearInterval(heartbeatTimer)
    heartbeatTimer = null
    // close any open writing sessions
    for (const src of Object.keys(writingState.value)) {
      if (writingState.value[src]) writingEnd(src)
    }
  }

  onBeforeUnmount(stop)

  return {
    writingState,
    writingStart,
    writingEnd,
    heartbeat,
    start,
    stop,
  }
}

export default useActivityTracking

/**
 * Global, app-wide activity tracking for Sales users.
 *
 * Installs a single `input` listener on `document` (capture phase) so ANY
 * typing in ANY <input>, <textarea> or contenteditable across the app
 * generates writing_start / writing_end events. Also installs a
 * click/keydown listener that occasionally emits `idle_end` — so idle
 * segments don't stay "open" on the Day Tape after the user comes back.
 *
 * Call once from App.vue after the user is authenticated. Returns a
 * cleanup function.
 */
// --- Work-page tracking ------------------------------------------------------
// Which page prefixes we consider "active CRM/work" for the Day Tape.
// Matched against location.pathname with startsWith.
const WORK_ROUTE_PREFIXES = [
  '/crm/',
  '/jobs',
  '/dispatch',
]
// work_active cadence. Must stay well below the Day Tape work gap
// (WORK_GAP_SEC = 60s in activity_service.py) — prod runs
// sales.heartbeat_interval_ms at 60000, so this tick cannot follow that
// setting without shattering continuous "working" segments.
const PRESENCE_TICK_MS = 20_000
const WORK_ACTIVE_WINDOW_MS = 45_000   // user must have interacted within this

function _isWorkRoute() {
  const p = location.pathname
  return WORK_ROUTE_PREFIXES.some(pref => p.startsWith(pref))
}

let _globalInstalled = false
let _globalApi = null
export function setupGlobalSalesTracking() {
  // Tech App Preview simulator: no presence/writing pings — read-only tokens
  // get 403 on every POST, and live mode must not fake the tech's presence.
  if (isTechPreview()) return () => {}
  if (_globalInstalled) return () => {}
  _globalInstalled = true

  // We don't use the hook-style API (no onBeforeUnmount) — manual wiring.
  const writing = {}
  const debounceTimers = {}
  let heartbeatTimer = null
  let lastIdleEndAt = 0

  const safe = (fn) => { try { return fn() } catch (e) { console.warn('[sales/activity]', e) } }

  function _ping(ev, extra) {
    const body = { event_type: ev }
    if (extra) body.extra = extra
    safe(() => apiClient.post('/sales/activity/ping/', body))
  }
  function maybeIdleEnd() {
    const now = Date.now()
    if (now - lastIdleEndAt < IDLE_END_MIN_INTERVAL_MS) return
    lastIdleEndAt = now
    _ping('idle_end')
  }
  function wEnd(source) {
    if (!writing[source]) return
    writing[source] = false
    clearTimeout(debounceTimers[source])
    safe(() => salesApi.writing(false, source))
  }
  function wStart(source) {
    maybeIdleEnd()
    if (writing[source]) {
      clearTimeout(debounceTimers[source])
      debounceTimers[source] = setTimeout(() => wEnd(source), runtimeConfig.writingDebounce)
      return
    }
    writing[source] = true
    safe(() => salesApi.writing(true, source))
    debounceTimers[source] = setTimeout(() => wEnd(source), runtimeConfig.writingDebounce)
  }

  function onInput(ev) {
    const t = ev?.target
    if (!t) return
    const tag = (t.tagName || '').toLowerCase()
    if (tag !== 'input' && tag !== 'textarea' && !t.isContentEditable) return
    const src = t.dataset?.trackSource
      || t.getAttribute?.('name')
      || (location.pathname.split('/').filter(Boolean)[0] || 'app')
    wStart(src)
  }

  function onActive() {
    // Any click/keydown means user is active → close any open idle.
    maybeIdleEnd()
  }

  // Track "active" moments for the work-page pinger (any real interaction).
  let lastActiveAt = Date.now()
  function markActive() { lastActiveAt = Date.now() }

  document.addEventListener('input', onInput, true)
  document.addEventListener('click', onActive, true)
  document.addEventListener('keydown', onActive, true)
  document.addEventListener('mousemove', markActive, { passive: true, capture: true })
  document.addEventListener('scroll',   markActive, { passive: true, capture: true })
  document.addEventListener('wheel',    markActive, { passive: true, capture: true })
  document.addEventListener('keydown',  markActive, true)
  document.addEventListener('click',    markActive, true)
  document.addEventListener('input',    markActive, true)

  // One timer instead of two. work_active keeps its own 20s cadence (see
  // PRESENCE_TICK_MS); page_heartbeat is throttled to
  // sales.heartbeat_interval_ms. Any ping already proves the tab is alive, so
  // a tick that sent work_active does not also send a heartbeat — that is
  // where the saved request volume comes from.
  let lastPingAt = 0
  function emitPresenceTick() {
    if (document.visibilityState !== 'visible') return
    const now = Date.now()
    if (_isWorkRoute() && now - lastActiveAt <= WORK_ACTIVE_WINDOW_MS) {
      lastPingAt = now
      _ping('work_active', { path: location.pathname })
      return
    }
    if (now - lastPingAt < runtimeConfig.heartbeat) return
    lastPingAt = now
    _ping('page_heartbeat')
  }

  _loadConfigOnce().finally(() => {
    emitPresenceTick()
    if (heartbeatTimer) clearInterval(heartbeatTimer)
    heartbeatTimer = setInterval(emitPresenceTick, PRESENCE_TICK_MS)
  })
  // Fire once soon after setup so the first tick window isn't blank.
  setTimeout(emitPresenceTick, 1000)

  _globalApi = {
    destroy() {
      document.removeEventListener('input', onInput, true)
      document.removeEventListener('click', onActive, true)
      document.removeEventListener('keydown', onActive, true)
      document.removeEventListener('mousemove', markActive, true)
      document.removeEventListener('scroll',   markActive, true)
      document.removeEventListener('wheel',    markActive, true)
      if (heartbeatTimer) clearInterval(heartbeatTimer)
      heartbeatTimer = null
      for (const s of Object.keys(writing)) {
        if (writing[s]) wEnd(s)
      }
      _globalInstalled = false
    },
  }
  return _globalApi.destroy
}
